import React, { useEffect, useMemo, useState } from 'react';

import { Badge, Button, Group, Select, SegmentedControl, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { type TrackPoint } from '../../types';
import { formatMetricLabel, formatMetricLabelNoUnit } from '../../utils/metricLabels';

import './PerformanceBucketsPanel.css';
import './themeInversion.css';

type WindFilter = 'all' | 'upwind' | 'downwind';

type PercentileRange = '0-100' | '50-100' | '70-100';

type BucketRow = {
  bucketStart: number;
  bucketEnd: number;
  label: string;
  rangeValue: [number | null, number | null];
  minValue: number | null;
  maxValue: number | null;
  avgValue: number | null;
  p90Value: number | null;
  count: number;
};

const RESERVED_KEYS = new Set(['utc', 'lat', 'lon', 'globalIndex']);

const COLOR_RANGE_FILL = 'rgba(59, 130, 246, 0.18)'; // soft blue band
const COLOR_RANGE_STROKE = 'rgba(59, 130, 246, 0.42)';
const COLOR_AVG_LINE = 'var(--chart-1, #2f72ff)'; // navy/blue for average
const COLOR_TARGET_LINE = 'var(--chart-2, #49b36d)'; // green for target
const AXIS_STROKE = 'var(--borderSubtle)';
const TICK_COLOR = 'var(--textMutedStrong)';
const PANEL_BG = 'var(--inverted-surface-muted, #ffffff)';

const coerceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMetricValue = (point: TrackPoint, key: string): number | null => {
  const value = (point as unknown as Record<string, unknown>)[key];
  return coerceNumber(value);
};

const isValidValue = (value: number | null, maxAbs = 1e4) =>
  value !== null && Number.isFinite(value) && Math.abs(value) < maxAbs;

const quantile = (values: number[], q: number): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const clampedQ = Math.min(Math.max(q, 0), 1);
  const index = (sorted.length - 1) * clampedQ;
  const low = Math.floor(index);
  const high = Math.ceil(index);
  if (low === high) {
    return sorted[low];
  }
  const weight = index - low;
  return sorted[low] * (1 - weight) + sorted[high] * weight;
};

const applyPercentileWindow = (
  values: number[],
  minPercent: number,
  maxPercent: number,
): number[] => {
  if (values.length === 0) {
    return [];
  }
  const sorted = [...values].sort((a, b) => a - b);
  const startIndex = Math.max(0, Math.floor((minPercent / 100) * (sorted.length - 1)));
  const endIndex = Math.min(
    sorted.length - 1,
    Math.floor((maxPercent / 100) * (sorted.length - 1)),
  );
  return sorted.slice(startIndex, endIndex + 1);
};

const getTwaValue = (point: TrackPoint): number | null => {
  const twaAbs = coerceNumber(point.twa_abs);
  if (twaAbs !== null) {
    return twaAbs;
  }
  return coerceNumber(point.twa);
};

const formatNumber = (value: number | null, digits = 2): string =>
  value === null || Number.isNaN(value) ? '–' : value.toFixed(digits);

const formatTick1 = (value: number) => {
  if (!Number.isFinite(value)) {
    return '';
  }
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
};

export interface PerformanceBucketsPanelProperties {
  trackData: TrackPoint[];
}

export const PerformanceBucketsPanel: React.FC<PerformanceBucketsPanelProperties> = ({
  trackData,
}) => {
  const { t } = useTranslation('common');
  const [metric, setMetric] = useState<string>('bsp');
  const [percentileRange, setPercentileRange] = useState<PercentileRange>('0-100');
  const [windFilter, setWindFilter] = useState<WindFilter>('all');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const percentileOptions = useMemo(
    () => [
      {
        value: '0-100' as PercentileRange,
        min: 0,
        max: 100,
        label: t('race.performanceBuckets.controls.percentileAll', 'All (0–100%)'),
      },
      {
        value: '50-100' as PercentileRange,
        min: 50,
        max: 100,
        label: t('race.performanceBuckets.controls.percentileBest50', 'Best 50% (50–100%)'),
      },
      {
        value: '70-100' as PercentileRange,
        min: 70,
        max: 100,
        label: t('race.performanceBuckets.controls.percentileBest30', 'Best 30% (70–100%)'),
      },
    ],
    [t],
  );

  const availableMetrics = useMemo(() => {
    const set = new Set<string>();
    for (const point of trackData) {
      for (const [key, value] of Object.entries(point)) {
        if (RESERVED_KEYS.has(key)) {
          continue;
        }
        if (coerceNumber(value) !== null) {
          set.add(key);
        }
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [trackData]);

  useEffect(() => {
    if (availableMetrics.length === 0) {
      return;
    }
    setMetric((previous) => (availableMetrics.includes(previous) ? previous : availableMetrics[0]));
  }, [availableMetrics]);

  const twaKey = useMemo(() => {
    if (trackData.some((point) => typeof point.twa_abs === 'number')) {
      return 'twa_abs';
    }
    if (trackData.some((point) => typeof point.twa === 'number')) {
      return 'twa';
    }
    return null;
  }, [trackData]);

  const { buckets, totalSamples } = useMemo(() => {
    if (trackData.length === 0 || !metric) {
      return { buckets: [] as BucketRow[], totalSamples: 0 };
    }

    const percConfig = percentileOptions.find((option) => option.value === percentileRange) ?? {
      min: 0,
      max: 100,
    };

    const filteredPoints = trackData.filter((point) => {
      const metricVal = getMetricValue(point, metric);
      const tws = coerceNumber(point.tws);
      if (!isValidValue(metricVal) || !isValidValue(tws, 300)) {
        return false;
      }
      if (windFilter === 'all' || !twaKey) {
        return true;
      }
      const twa = getTwaValue(point);
      if (twa === null) {
        return true;
      }
      if (windFilter === 'upwind') {
        return twa <= 100;
      }
      return twa >= 90;
    });

    if (filteredPoints.length === 0) {
      return { buckets: [] as BucketRow[], totalSamples: 0 };
    }

    const twsValues = filteredPoints
      .map((point) => coerceNumber(point.tws))
      .filter((value): value is number => value !== null);

    if (twsValues.length === 0) {
      return { buckets: [] as BucketRow[], totalSamples: 0 };
    }

    const bucketWidth = 1;
    const minBucket = Math.floor(Math.min(...twsValues) / bucketWidth) * bucketWidth;
    const maxBucket = Math.ceil(Math.max(...twsValues) / bucketWidth) * bucketWidth;

    let rows: BucketRow[] = [];
    for (let start = minBucket; start <= maxBucket; start += bucketWidth) {
      const end = start + bucketWidth;
      const values = filteredPoints
        .filter((point) => {
          const tws = coerceNumber(point.tws);
          if (!isValidValue(tws, 300)) {
            return false;
          }
          const safeTws = tws as number;
          return safeTws >= start && safeTws < end;
        })
        .map((point) => getMetricValue(point, metric))
        .filter((value): value is number => isValidValue(value));

      if (values.length === 0) {
        rows.push({
          bucketStart: start,
          bucketEnd: end,
          label: `${start}–${end}`,
          rangeValue: [null, null],
          minValue: null,
          maxValue: null,
          avgValue: null,
          p90Value: null,
          count: 0,
        });
        continue;
      }

      const windowValues = applyPercentileWindow(values, percConfig.min, percConfig.max);
      const minValue = Math.min(...windowValues);
      const maxValue = Math.max(...windowValues);
      const avgValue = windowValues.reduce((acc, value) => acc + value, 0) / windowValues.length;
      const p90Value = quantile(values, 0.9);

      rows.push({
        bucketStart: start,
        bucketEnd: end,
        label: `${start}–${end}`,
        rangeValue: [minValue, maxValue],
        minValue,
        maxValue,
        avgValue,
        p90Value,
        count: values.length,
      });
    }

    // Trim empty buckets only at the extremes to avoid gaps in the middle
    while (rows.length > 0 && rows[0].count === 0) {
      rows = rows.slice(1);
    }
    while (rows.length > 0 && (rows.at(-1)?.count ?? 0) === 0) {
      rows = rows.slice(0, -1);
    }

    return { buckets: rows, totalSamples: filteredPoints.length };
  }, [metric, percentileOptions, percentileRange, trackData, twaKey, windFilter]);

  const yDomain = useMemo<[number, number] | ['auto', 'auto']>(() => {
    const mins = buckets
      .map((bucket) => bucket.minValue)
      .filter((value): value is number => isValidValue(value));
    const maxs = buckets
      .map((bucket) => bucket.maxValue)
      .filter((value): value is number => isValidValue(value));
    if (mins.length === 0 || maxs.length === 0) {
      return ['auto', 'auto'] as const;
    }
    const min = Math.min(...mins);
    const max = Math.max(...maxs);
    const span = Math.max(0.1, max - min);
    const padding = span * 0.15;
    return [Math.max(0, min - padding), max + padding] as const;
  }, [buckets]);

  const noData = buckets.every(
    (bucket) =>
      bucket.minValue === null &&
      bucket.maxValue === null &&
      bucket.avgValue === null &&
      bucket.p90Value === null,
  );

  return (
    <div className="performance-panel theme-inverted" data-mantine-color-scheme="light">
      <div className="performance-header">
        <Text fw={700} size="lg">
          {t('race.performanceBuckets.title', 'Targets')}
        </Text>
        <Button
          size="xs"
          variant="light"
          className="performance-toggle"
          onClick={() => setSettingsOpen((open) => !open)}
        >
          {settingsOpen
            ? t('race.scatter.controls.hide', 'Hide settings')
            : t('race.scatter.controls.show', 'Show settings')}
        </Button>
      </div>

      {settingsOpen && (
        <div className="performance-card controls">
          <div className="control">
            <label>{t('race.performanceBuckets.controls.metric', 'Metric')}</label>
            <Select
              placeholder={t('race.performanceBuckets.controls.metricPlaceholder', 'Select metric')}
              data={availableMetrics.map((item) => ({
                value: item,
                label: formatMetricLabel(item),
              }))}
              value={metric}
              onChange={(value) => value && setMetric(value)}
              searchable
            />
          </div>

          <div className="control">
            <label>{t('race.performanceBuckets.controls.percentiles', 'Percentiles')}</label>
            <Select
              data={percentileOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={percentileRange}
              onChange={(value) => value && setPercentileRange(value as PercentileRange)}
            />
          </div>

          <div className="control wind-control">
            <label>{t('race.performanceBuckets.controls.wind', 'Wind mode')}</label>
            <SegmentedControl
              fullWidth
              className="wind-segment"
              value={windFilter}
              onChange={(value) => setWindFilter(value as WindFilter)}
              data={[
                { value: 'all', label: t('race.performanceBuckets.wind.all', 'All') },
                { value: 'upwind', label: t('race.performanceBuckets.wind.upwind', 'Upwind') },
                { value: 'downwind', label: t('race.performanceBuckets.wind.downwind', 'Downwind') },
              ]}
            />
          </div>
        </div>
      )}

      <div className="performance-card chart-card">
        <div className="chart-header">
          <Group gap="xs">
            <Badge variant="light" color="indigo">
              {formatMetricLabel(metric || 'metric')}
            </Badge>
          </Group>
          <Group gap="sm">
            <div className="legend-item">
              <span className="legend-swatch band" />
              <span>{t('race.performanceBuckets.legend.range', 'Range (min–max)')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-swatch avg" />
              <span>{t('race.performanceBuckets.legend.average', 'Average')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-swatch target" />
              <span>{t('race.performanceBuckets.legend.target', 'Target (p90)')}</span>
            </div>
          </Group>
        </div>

        <div className="chart-body">
          {noData ? (
            <div className="chart-empty">
              {t(
                'race.performanceBuckets.empty',
                'No data available for this metric or filters. Try another metric.',
              )}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={buckets}
                margin={{
                  top: 12,
                  right: 16,
                  left: 6,
                  bottom: 12,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--borderSubtle)" />
                <XAxis
                  dataKey="bucketStart"
                  tickFormatter={formatTick1}
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_COLOR }}
                  tickLine={{ stroke: AXIS_STROKE }}
                  axisLine={{ stroke: AXIS_STROKE }}
                  label={{
                    value: t('race.performanceBuckets.axis.tws', 'TWS Bucket'),
                    position: 'insideBottom',
                    offset: -8,
                    fill: TICK_COLOR,
                  }}
                />
                <YAxis
                  stroke={AXIS_STROKE}
                  tick={{ fill: TICK_COLOR }}
                  tickFormatter={formatTick1}
                  tickLine={{ stroke: AXIS_STROKE }}
                  axisLine={{ stroke: AXIS_STROKE }}
                  label={{
                    value: formatMetricLabelNoUnit(metric || 'metric'),
                    angle: -90,
                    position: 'insideLeft',
                    dy: 24,
                    fill: TICK_COLOR,
                  }}
                  domain={[...yDomain]}
                  width={52}
                />
                <Tooltip
                  formatter={(value: ValueType, key: string) => {
                    const labels: Record<string, string> = {
                      rangeValue: t('race.performanceBuckets.tooltip.range', 'Range'),
                      minValue: t('race.performanceBuckets.tooltip.min', 'Min'),
                      maxValue: t('race.performanceBuckets.tooltip.max', 'Max'),
                      avgValue: t('race.performanceBuckets.tooltip.avg', 'Avg'),
                      p90Value: t('race.performanceBuckets.tooltip.p90', 'P90'),
                    };
                    if (Array.isArray(value)) {
                      const [min, max] = value as Array<number | null>;
                      return [
                        `${formatNumber(min ?? null, 2)} – ${formatNumber(max ?? null, 2)}`,
                        labels[key] ?? key,
                      ];
                    }
                    const numericValue = typeof value === 'number' ? value : Number(value);
                    return [
                      formatNumber(Number.isFinite(numericValue) ? numericValue : null, 2),
                      labels[key] ?? key,
                    ];
                  }}
                  labelFormatter={(_, payload) => {
                    const bucket = payload?.[0]?.payload as BucketRow | undefined;
                    return bucket ? bucket.label : '';
                  }}
                  filterNull={false}
                  itemSorter={(item: Payload<ValueType, string>) => {
                    const order: Record<string, number> = {
                      rangeValue: 1,
                      maxValue: 2,
                      minValue: 3,
                      p90Value: 4,
                      avgValue: 5,
                    };
                    return order[item.dataKey as string] ?? 99;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="rangeValue"
                  isRange
                  stroke={COLOR_RANGE_STROKE}
                  strokeWidth={1.4}
                  fill={COLOR_RANGE_FILL}
                  fillOpacity={1}
                  connectNulls
                  isAnimationActive={false}
                  activeDot={false}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="avgValue"
                  stroke={COLOR_AVG_LINE}
                  strokeWidth={3}
                  dot={{
                    r: 3.5,
                    strokeWidth: 1,
                    stroke: 'var(--inverted-surface-muted, #f8fafc)',
                    fill: COLOR_AVG_LINE,
                  }}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="p90Value"
                  stroke={COLOR_TARGET_LINE}
                  strokeWidth={2.4}
                  strokeDasharray="4 3"
                  dot={{
                    r: 3.5,
                    strokeWidth: 1,
                    stroke: 'var(--inverted-surface-muted, #f8fafc)',
                    fill: COLOR_TARGET_LINE,
                  }}
                  connectNulls
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  );
};
